// Avatar builder v2 parts: face & body shapes, eye shapes, lashes, brows,
// lips/makeup, facial hair, fantasy features and new garments/shoes.
//
// Everything here is optional on a BuilderSelection: an old selection (or an
// old saved recipe) without these fields builds exactly as before.
// Options are presented as neutral Face / Body choices: nothing is tied to a
// base body or a gender.
//
// No runtime imports from catalog.ts (it imports this file).

import { normalizeLibrary, type LibSelection } from './garmentCatalog';

export const EXTRAS_ASSETS = 'extras/';

/** Face-shape sliders, each -1..1 (0 = the base's own face). */
export interface FaceSliders { jaw: number; chin: number; cheeks: number; length: number; forehead: number; nose: number; lips: number; bridge: number; cheekbones: number }
/** Body-shape sliders: height -1..1, build -1..1 (slim..broad), curves 0..1, fullness 0..1, muscle 0..1. */
export interface BodySliders { height: number; build: number; curves: number; fullness: number; muscle: number }

export interface BuilderExtras {
  faceShape: string;
  /** Feature preset (nose / eyes / cheekbones / lips), added on top of the face shape. */
  features: string;
  face: FaceSliders;
  bodyShape: string;
  body: BodySliders;
  eyeShape: string;
  lashes: string;
  brows: string;
  lips: string;
  eyeMakeup: string;
  freckles: string;
  facialHair: string;
  beardTexture: string;
  wings: string;
  horns: string;
  ears: string;
  halo: string;
  tail: string;
  markings: string;
  fantasyColor: string;
  top: string;
  bottom: string;
  shoes: string;
  over: string;
  topColor: string;
  bottomColor: string;
  shoeColor: string;
  overColor: string;
  /** Garment library pieces by layering slot (garmentCatalog.ts). Empty = the base outfit / v2 garments. */
  lib: LibSelection;
}

export interface Named { id: string; name: string; tags?: string }

// ---------- shapes ----------

export const FACE_ZERO: FaceSliders = { jaw: 0, chin: 0, cheeks: 0, length: 0, forehead: 0, nose: 0, lips: 0, bridge: 0, cheekbones: 0 };
export const FACE_SLIDERS: { key: keyof FaceSliders; name: string; lo: string; hi: string }[] = [
  { key: 'jaw', name: 'Jaw', lo: 'Narrow', hi: 'Strong' },
  { key: 'chin', name: 'Chin', lo: 'Short', hi: 'Long' },
  { key: 'cheeks', name: 'Cheeks', lo: 'Hollow', hi: 'Full' },
  { key: 'length', name: 'Face length', lo: 'Short', hi: 'Long' },
  { key: 'forehead', name: 'Forehead', lo: 'Narrow', hi: 'Wide' },
  { key: 'nose', name: 'Nose', lo: 'Small', hi: 'Broad' },
  { key: 'lips', name: 'Lips', lo: 'Thin', hi: 'Full' },
  { key: 'bridge', name: 'Nose bridge', lo: 'Low / flat', hi: 'High' },
  { key: 'cheekbones', name: 'Cheekbones', lo: 'Soft', hi: 'High' },
];
export interface FaceShape extends Named { face: FaceSliders }
const fs = (o: Partial<FaceSliders>): FaceSliders => ({ ...FACE_ZERO, ...o });
export const FACE_SHAPES: FaceShape[] = [
  { id: 'base', name: 'Classic', tags: 'default, balanced', face: fs({}) },
  { id: 'oval', name: 'Oval', tags: 'oval face, balanced, slightly long', face: fs({ length: 0.3, jaw: -0.15, cheeks: 0.1 }) },
  { id: 'round', name: 'Round', tags: 'round face, soft, wide cheeks, short chin', face: fs({ cheeks: 0.75, jaw: 0.35, chin: -0.45, length: -0.35 }) },
  { id: 'heart', name: 'Heart', tags: 'heart-shaped face, wide forehead, pointed chin, narrow jaw', face: fs({ forehead: 0.6, jaw: -0.6, chin: 0.25, cheeks: 0.25 }) },
  { id: 'square', name: 'Square', tags: 'square face, strong angular jaw, flat chin', face: fs({ jaw: 0.9, chin: -0.1, cheeks: 0.1, forehead: 0.3 }) },
  { id: 'long', name: 'Long', tags: 'long face, oblong, long chin', face: fs({ length: 0.8, chin: 0.5, cheeks: -0.25, jaw: -0.1 }) },
  { id: 'full', name: 'Soft & full', tags: 'full cheeks, soft rounded jaw, plump face', face: fs({ cheeks: 1, jaw: 0.5, chin: -0.2, lips: 0.4 }) },
  { id: 'diamond', name: 'Diamond', tags: 'high cheekbones, narrow forehead, narrow jaw', face: fs({ cheeks: 0.45, forehead: -0.5, jaw: -0.45, chin: 0.3, cheekbones: 0.4 }) },
];

/**
 * Feature presets: nose, bridge, cheekbones, lips and an eye shape, named by
 * the features themselves (never by a group of people). Their sliders add on
 * top of the face shape's; picking one in the UI also sets `eye` as the eye shape.
 */
export interface FeaturePreset extends Named { face: Partial<FaceSliders>; eye?: string }
export const FEATURE_PRESETS: FeaturePreset[] = [
  { id: 'none', name: 'As shaped', face: {} },
  { id: 'broad-full', name: 'Broad nose, full lips', tags: 'broad nose, wide nostrils, low flat bridge, full lips', face: { nose: 0.75, bridge: -0.45, lips: 0.85, cheeks: 0.1 } },
  { id: 'soft-monolid', name: 'Soft monolid', tags: 'monolid eyes, soft low nose bridge, flat cheeks, smooth', face: { bridge: -0.55, nose: 0.1, cheekbones: 0.15, cheeks: 0.15 }, eye: 'monolid' },
  { id: 'hooded-almond', name: 'Hooded almond', tags: 'hooded almond eyes, medium bridge, softly full lips', face: { bridge: -0.15, lips: 0.35, cheekbones: 0.25 }, eye: 'hooded-almond' },
  { id: 'inner-fold', name: 'Inner fold, lifted', tags: 'epicanthic inner fold, upturned eyes, low bridge, small nose', face: { bridge: -0.4, nose: -0.2, cheekbones: 0.35 }, eye: 'inner-fold' },
  { id: 'high-cheekbones', name: 'High cheekbones', tags: 'high prominent cheekbones, strong straight nose, defined jaw', face: { cheekbones: 0.9, bridge: 0.35, jaw: 0.2, cheeks: -0.2 }, eye: 'almond' },
  { id: 'high-bridge', name: 'High bridge, deep-set', tags: 'high nose bridge, prominent nose, deep-set eyes, thick brows', face: { bridge: 0.9, nose: 0.25, lips: 0.1 }, eye: 'deep-set' },
  { id: 'aquiline', name: 'Strong arched nose', tags: 'strong arched aquiline nose, almond eyes, full lips', face: { bridge: 0.7, nose: 0.45, lips: 0.4, cheekbones: 0.2 }, eye: 'almond' },
  { id: 'wide-nose-high-cheek', name: 'Wide nose, high cheeks', tags: 'wide rounded nose, high wide cheekbones, broad face, full lips', face: { nose: 0.6, bridge: -0.2, cheekbones: 0.7, cheeks: 0.35, lips: 0.5 } },
  { id: 'narrow-fine', name: 'Narrow, fine features', tags: 'narrow straight nose, thin lips, round eyes, fine features', face: { nose: -0.45, bridge: 0.5, lips: -0.45, cheekbones: 0.1 }, eye: 'round' },
  { id: 'round-soft', name: 'Round & soft', tags: 'round soft features, button nose, full cheeks, smiling eyes', face: { nose: -0.15, bridge: -0.3, cheeks: 0.5, lips: 0.3 }, eye: 'smiling' },
  { id: 'monolid-lifted', name: 'Lifted monolid', tags: 'upturned monolid eyes, high cheekbones, straight nose', face: { bridge: -0.2, cheekbones: 0.6 }, eye: 'monolid-lifted' },
];

export const BODY_ZERO: BodySliders = { height: 0, build: 0, curves: 0, fullness: 0, muscle: 0 };
export const BODY_SLIDERS: { key: keyof BodySliders; name: string; min: number; lo: string; hi: string }[] = [
  { key: 'height', name: 'Height', min: -1, lo: 'Shorter', hi: 'Taller' },
  { key: 'build', name: 'Build', min: -1, lo: 'Slim', hi: 'Broad' },
  { key: 'curves', name: 'Curves', min: 0, lo: 'Straight', hi: 'Curvy' },
  { key: 'fullness', name: 'Fullness', min: 0, lo: 'Lean', hi: 'Full' },
  { key: 'muscle', name: 'Muscle', min: 0, lo: 'Soft', hi: 'Muscular' },
];
export interface BodyShape extends Named { body: BodySliders }
const bs = (o: Partial<BodySliders>): BodySliders => ({ ...BODY_ZERO, ...o });
export const BODY_SHAPES: BodyShape[] = [
  { id: 'base', name: 'Classic', tags: 'default, average', body: bs({}) },
  { id: 'slim', name: 'Slim', tags: 'slim, slender, lean', body: bs({ build: -0.7 }) },
  { id: 'athletic', name: 'Athletic', tags: 'athletic, fit, toned, sporty', body: bs({ build: 0.25, muscle: 0.55 }) },
  { id: 'hourglass', name: 'Curvy hourglass', tags: 'curvy, hourglass, full hips and bust, defined waist', body: bs({ curves: 1, fullness: 0.25 }) },
  { id: 'full', name: 'Full-figured', tags: 'plus size, full figured, big, fuller body, heavy set', body: bs({ fullness: 1, curves: 0.45, build: 0.2 }) },
  { id: 'broad', name: 'Broad & strong', tags: 'broad shoulders, muscular, strong, stocky', body: bs({ build: 0.85, muscle: 1 }) },
  { id: 'petite', name: 'Petite', tags: 'petite, short, small frame', body: bs({ height: -0.85, build: -0.35 }) },
  { id: 'tall', name: 'Tall', tags: 'tall, long legs, lanky', body: bs({ height: 0.9, build: -0.1 }) },
];

// ---------- eyes / brows / lashes ----------

/** Baked VRoid eye morphs (fraction of each target, by name suffix). */
export interface EyeShape extends Named {
  bake: Record<string, number>;
  /** Extra upper-lid closing at the inner corner (fraction of EYE_Close), tapering to 0 at the outer corner: an inner fold. */
  innerClose?: number;
  /** 0 = paint out the upper-lid crease line (monolid); default keeps it. */
  crease?: number;
}
export const EYE_SHAPES: EyeShape[] = [
  { id: 'base', name: 'Classic', bake: {} },
  { id: 'almond', name: 'Almond', tags: 'almond eyes', bake: { EYE_Angry: 0.3, EYE_Close: 0.1 } },
  { id: 'round', name: 'Round', tags: 'big round eyes, wide eyes', bake: { EYE_Surprised: 0.6 } },
  { id: 'upturned', name: 'Upturned', tags: 'upturned eyes, cat eyes, lifted outer corner', bake: { EYE_Angry: 0.5 } },
  { id: 'soft', name: 'Soft / downturned', tags: 'downturned eyes, gentle, soft', bake: { EYE_Sorrow: 0.55 } },
  { id: 'hooded', name: 'Hooded', tags: 'hooded eyes, heavy lids, sleepy, monolid', bake: { EYE_Close: 0.3 } },
  { id: 'smiling', name: 'Smiling', tags: 'smiling eyes, cheerful', bake: { EYE_Joy: 0.3 } },
  { id: 'monolid', name: 'Soft monolid', tags: 'monolid, single eyelid, no crease, soft narrow eyes', bake: { EYE_Close: 0.16 }, innerClose: 0.14, crease: 0 },
  { id: 'monolid-lifted', name: 'Lifted monolid', tags: 'upturned monolid, single eyelid, lifted outer corner', bake: { EYE_Close: 0.1, EYE_Angry: 0.35 }, innerClose: 0.12, crease: 0 },
  { id: 'hooded-almond', name: 'Hooded almond', tags: 'hooded almond eyes, heavy lid, faint crease', bake: { EYE_Close: 0.22, EYE_Angry: 0.18 }, crease: 0.35 },
  { id: 'inner-fold', name: 'Inner fold', tags: 'epicanthic fold, inner corner covered, slightly upturned', bake: { EYE_Close: 0.06, EYE_Angry: 0.22 }, innerClose: 0.3, crease: 0.4 },
  { id: 'deep-set', name: 'Deep-set', tags: 'deep-set eyes, strong brow, defined crease', bake: { EYE_Sorrow: 0.22, EYE_Close: 0.1 } },
  { id: 'wide-open', name: 'Wide open', tags: 'big wide open eyes, double eyelid', bake: { EYE_Surprised: 0.3, EYE_Joy: 0.05 } },
];

export const LASH_STYLES: Named[] = [
  { id: 'base', name: 'Body default' },
  { id: 'none', name: 'None' },
  { id: 'soft', name: 'Soft', tags: 'short subtle lashes' },
  { id: 'natural', name: 'Natural', tags: 'natural lashes' },
  { id: 'full', name: 'Full', tags: 'full thick lashes' },
  { id: 'dramatic', name: 'Dramatic', tags: 'long dramatic lashes, false lashes' },
];

export const BROW_STYLES: Named[] = [
  { id: 'base', name: 'Classic' },
  { id: 'soft-arch', name: 'Soft arch', tags: 'soft arched brows' },
  { id: 'straight', name: 'Straight', tags: 'straight flat brows' },
  { id: 'thick', name: 'Thick', tags: 'thick bold brows' },
  { id: 'bushy', name: 'Bushy', tags: 'bushy full natural brows' },
  { id: 'thin', name: 'Thin arch', tags: 'thin high arched brows' },
  { id: 'angled', name: 'Angled', tags: 'sharp angled brows' },
  { id: 'rounded', name: 'Rounded', tags: 'round curved brows' },
];

// ---------- makeup / facial hair ----------

/** Natural freckles over the nose and cheeks (painted, not glowing). */
export interface FreckleStyle extends Named { count: number; spread: number }
export const FRECKLES: FreckleStyle[] = [
  { id: 'none', name: 'None', count: 0, spread: 0 },
  { id: 'light', name: 'Light freckles', tags: 'a few freckles, light freckles', count: 70, spread: 0.8 },
  { id: 'full', name: 'Freckled', tags: 'lots of freckles, freckled face', count: 190, spread: 1.15 },
]; 

export interface LipStyle extends Named { hex: string | null; amount: number; gloss?: boolean }
export const LIP_STYLES: LipStyle[] = [
  { id: 'none', name: 'None', hex: null, amount: 0 },
  { id: 'natural', name: 'Natural', tags: 'natural lips', hex: '#8a4a42', amount: 0.35 },
  { id: 'brown', name: 'Brown nude', tags: 'brown nude lipstick', hex: '#7a4630', amount: 0.6 },
  { id: 'gloss', name: 'Gloss', tags: 'lip gloss, shiny', hex: '#b0675c', amount: 0.4, gloss: true },
  { id: 'berry', name: 'Berry', tags: 'berry lipstick', hex: '#7c2347', amount: 0.75 },
  { id: 'red', name: 'Classic red', tags: 'red lipstick', hex: '#b3182b', amount: 0.85 },
  { id: 'plum', name: 'Plum', tags: 'plum dark lipstick', hex: '#4e1b3a', amount: 0.8 },
  { id: 'coral', name: 'Coral', tags: 'coral orange lipstick', hex: '#e0604a', amount: 0.7 },
];

export interface EyeMakeup extends Named { liner?: 'line' | 'wing'; shadow?: string; shimmer?: boolean }
export const EYE_MAKEUP: EyeMakeup[] = [
  { id: 'none', name: 'None' },
  { id: 'liner', name: 'Eyeliner', tags: 'eyeliner', liner: 'line' },
  { id: 'wing', name: 'Winged liner', tags: 'winged eyeliner, cat eye', liner: 'wing' },
  { id: 'gold', name: 'Gold shimmer', tags: 'gold eyeshadow', shadow: '#d9a43a', shimmer: true },
  { id: 'smoky', name: 'Smoky', tags: 'smoky eye', shadow: '#3a2a33', liner: 'line' },
  { id: 'violet', name: 'Violet pop', tags: 'purple eyeshadow, colourful', shadow: '#8a4fd0', liner: 'wing' },
  { id: 'teal', name: 'Teal pop', tags: 'teal blue eyeshadow, colourful', shadow: '#1aa6a0' },
];

export interface FacialHair extends Named { shell: boolean; stubble: number }
export const FACIAL_HAIR: FacialHair[] = [
  { id: 'none', name: 'None', shell: false, stubble: 0 },
  { id: 'stubble', name: 'Stubble', tags: 'stubble, five o’clock shadow', shell: false, stubble: 0.7 },
  { id: 'moustache', name: 'Moustache', tags: 'moustache, mustache', shell: true, stubble: 0.25 },
  { id: 'goatee', name: 'Goatee', tags: 'goatee, chin beard and moustache', shell: true, stubble: 0.3 },
  { id: 'chin-strap', name: 'Chin strap', tags: 'chinstrap beard, jawline beard, line-up', shell: true, stubble: 0.2 },
  { id: 'short-beard', name: 'Short beard', tags: 'short boxed beard', shell: true, stubble: 0.35 },
  { id: 'full-beard', name: 'Full beard', tags: 'full beard, thick beard', shell: true, stubble: 0.4 },
  { id: 'long-beard', name: 'Long beard', tags: 'long big beard', shell: true, stubble: 0.4 },
];
export const BEARD_TEXTURES: Named[] = [
  { id: 'coily', name: 'Coily', tags: '4c coily beard' },
  { id: 'curly', name: 'Curly', tags: 'curly beard' },
  { id: 'wavy', name: 'Wavy', tags: 'wavy beard' },
  { id: 'straight', name: 'Straight', tags: 'straight beard' },
];
export const beardTextureAsset = (id: string) => `${EXTRAS_ASSETS}tex/beard-${id}.jpg`;

// ---------- fantasy ----------

export interface WingDef extends Named { tex: string; root: [number, number]; span: number; layers: number; tint: boolean }
export const WINGS: WingDef[] = ([
  { id: 'none', name: 'None', tex: '', root: [0, 0], span: 0, layers: 0, tint: false },
  { id: 'angel', name: 'Angel', tags: 'angel wings, white feathered wings', tex: 'wings/angel.png', root: [0.06, 0.92], span: 0.78, layers: 2, tint: true },
  { id: 'bat', name: 'Bat', tags: 'bat wings, demon wings, vampire', tex: 'wings/bat.png', root: [0.05, 0.9], span: 0.72, layers: 1, tint: false },
  { id: 'fairy', name: 'Fairy', tags: 'fairy wings, translucent, pixie', tex: 'wings/fairy.png', root: [0.03, 0.95], span: 0.5, layers: 2, tint: true },
  { id: 'butterfly', name: 'Butterfly', tags: 'butterfly wings, blue', tex: 'wings/butterfly.png', root: [0.02, 0.5], span: 0.55, layers: 1, tint: false },
  { id: 'dragon', name: 'Dragon', tags: 'dragon wings, scaly', tex: 'wings/dragon.png', root: [0.08, 0.92], span: 0.8, layers: 1, tint: false },
  { id: 'phoenix', name: 'Phoenix', tags: 'phoenix wings, fire wings, flame feathers', tex: 'wings/phoenix.png', root: [0.04, 0.9], span: 0.74, layers: 2, tint: false },
] as WingDef[]).map((w) => ({ ...w, tex: w.tex ? EXTRAS_ASSETS + w.tex : "" }));

export const HORNS: Named[] = [
  { id: 'none', name: 'None' },
  { id: 'ram', name: 'Ram', tags: 'curled ram horns, goat, satyr' },
  { id: 'devil', name: 'Devil', tags: 'small devil horns, demon, imp' },
  { id: 'antelope', name: 'Antelope', tags: 'long swept-back horns, oni, dragon horns' },
  { id: 'unicorn', name: 'Unicorn', tags: 'single unicorn horn' },
  { id: 'antlers', name: 'Antlers', tags: 'deer antlers, forest spirit' },
];
export const EARS: Named[] = [
  { id: 'none', name: 'Human' },
  { id: 'elf', name: 'Elf', tags: 'pointed elf ears' },
  { id: 'elf-long', name: 'Long elf', tags: 'long pointed elf ears' },
];
export const HALOS: Named[] = [
  { id: 'none', name: 'None' },
  { id: 'gold', name: 'Gold halo', tags: 'gold halo, angel' },
  { id: 'neon', name: 'Glow ring', tags: 'glowing neon halo ring' },
  { id: 'crown', name: 'Light crown', tags: 'glowing crown of light spikes' },
];
export const TAILS: Named[] = [
  { id: 'none', name: 'None' },
  { id: 'cat', name: 'Cat', tags: 'cat tail, feline' },
  { id: 'fox', name: 'Fox', tags: 'fluffy fox tail, kitsune' },
  { id: 'devil', name: 'Devil', tags: 'thin devil tail with spade tip' },
  { id: 'dragon', name: 'Dragon', tags: 'scaly dragon tail, lizard' },
];
export const MARKINGS: Named[] = [
  { id: 'none', name: 'None' },
  { id: 'constellation', name: 'Constellation', tags: 'glowing star dots on the cheeks' },
  { id: 'circuit', name: 'Circuit', tags: 'glowing cyber circuit lines, cyberpunk' },
  { id: 'third-eye', name: 'Third eye', tags: 'glowing forehead dot and brow dots, mystic' },
  { id: 'glow-freckles', name: 'Glow freckles', tags: 'glowing freckles' },
  { id: 'stripes', name: 'Cheek stripes', tags: 'glowing cheek stripes, war paint inspired' },
];
export interface Swatch { id: string; name: string; hex: string; metal?: boolean }
/** Accent colour for horns, halo glow, tail fur, markings and tintable wings. */
export const FANTASY_COLORS: Swatch[] = [
  { id: 'natural', name: 'Natural', hex: '#ffffff' },
  { id: 'gold', name: 'Gold', hex: '#e8b84a' },
  { id: 'cyan', name: 'Cyan', hex: '#38d7e8' },
  { id: 'violet', name: 'Violet', hex: '#9a6cff' },
  { id: 'rose', name: 'Rose', hex: '#ff6fa8' },
  { id: 'crimson', name: 'Crimson', hex: '#d0283c' },
  { id: 'emerald', name: 'Emerald', hex: '#2fbf71' },
  { id: 'onyx', name: 'Onyx', hex: '#2a2830' },
  { id: 'ivory', name: 'Ivory', hex: '#f1e6cf' },
];

// ---------- garments ----------

export type GarmentKind = 'top' | 'bottom' | 'shoes' | 'over';
export interface GarmentDef extends Named {
  kind: GarmentKind;
  /** Fabric tile under extras/fabric/, or a v1 pattern id (pat/…). */
  fabric: string;
  color: string;
  /** Fantasy / urban grouping for the picker. */
  group: 'urban' | 'fantasy' | 'everyday';
}
export const GARMENT_COLORS: Swatch[] = [
  { id: 'black', name: 'Black', hex: '#232326' },
  { id: 'charcoal', name: 'Charcoal', hex: '#45464c' },
  { id: 'grey', name: 'Heather grey', hex: '#9a9ca3' },
  { id: 'white', name: 'White', hex: '#f1efea' },
  { id: 'cream', name: 'Cream', hex: '#e8dcc4' },
  { id: 'khaki', name: 'Khaki', hex: '#a89870' },
  { id: 'olive', name: 'Olive', hex: '#5b6238' },
  { id: 'tan', name: 'Tan', hex: '#b9895a' },
  { id: 'brown', name: 'Brown', hex: '#5e3d26' },
  { id: 'navy', name: 'Navy', hex: '#26314f' },
  { id: 'royal', name: 'Royal blue', hex: '#2f4fb5' },
  { id: 'teal', name: 'Teal', hex: '#1f7f80' },
  { id: 'forest', name: 'Forest', hex: '#2c5a3a' },
  { id: 'red', name: 'Red', hex: '#b3261e' },
  { id: 'burgundy', name: 'Burgundy', hex: '#6d1f30' },
  { id: 'purple', name: 'Purple', hex: '#5d3592' },
  { id: 'pink', name: 'Pink', hex: '#e58fb0' },
  { id: 'mustard', name: 'Mustard', hex: '#d1a22a' },
  { id: 'orange', name: 'Orange', hex: '#df6c26' },
  { id: 'steel', name: 'Steel', hex: '#c9ced6', metal: true },
  { id: 'gold', name: 'Gold', hex: '#d9b04a', metal: true },
];
export const GARMENTS: GarmentDef[] = [
  // tops
  { id: 'tee', kind: 'top', name: 'Fitted tee', tags: 't-shirt, tee, casual', fabric: 'fabric/nylon', color: 'white', group: 'everyday' },
  { id: 'tracksuit-top', kind: 'top', name: 'Track jacket', tags: 'tracksuit jacket, athletic, sporty', fabric: 'fabric/nylon', color: 'red', group: 'urban' },
  { id: 'bomber', kind: 'top', name: 'Bomber jacket', tags: 'bomber jacket, flight jacket', fabric: 'fabric/nylon', color: 'olive', group: 'urban' },
  { id: 'puffer', kind: 'top', name: 'Puffer jacket', tags: 'puffer jacket, down jacket, winter', fabric: 'fabric/puffer', color: 'black', group: 'urban' },
  { id: 'denim-jacket', kind: 'top', name: 'Denim jacket', tags: 'denim jacket, jean jacket', fabric: 'pat/denim', color: 'white', group: 'urban' },
  { id: 'leather-jacket', kind: 'top', name: 'Leather jacket', tags: 'leather jacket, biker, moto', fabric: 'fabric/leather', color: 'black', group: 'urban' },
  { id: 'long-coat', kind: 'top', name: 'Long coat', tags: 'long trench coat, overcoat', fabric: 'fabric/wool', color: 'tan', group: 'urban' },
  { id: 'tunic', kind: 'top', name: 'Adventurer tunic', tags: 'fantasy tunic, ranger, medieval', fabric: 'fabric/wool', color: 'forest', group: 'fantasy' },
  { id: 'armor', kind: 'top', name: 'Plate armour', tags: 'knight armour, plate armor, paladin, warrior', fabric: 'fabric/armor', color: 'steel', group: 'fantasy' },
  { id: 'chainmail', kind: 'top', name: 'Chainmail', tags: 'chainmail, mail shirt, medieval', fabric: 'fabric/chainmail', color: 'steel', group: 'fantasy' },
  { id: 'robe', kind: 'top', name: 'Mage robe', tags: 'wizard robe, mage, sorcerer, priest, long robe', fabric: 'fabric/brocade', color: 'purple', group: 'fantasy' },
  // bottoms
  { id: 'leggings', kind: 'bottom', name: 'Leggings', tags: 'leggings, tights, yoga pants', fabric: 'fabric/nylon', color: 'black', group: 'everyday' },
  { id: 'track-pants', kind: 'bottom', name: 'Track pants', tags: 'tracksuit pants, joggers, sweatpants', fabric: 'fabric/nylon', color: 'red', group: 'urban' },
  { id: 'cargo', kind: 'bottom', name: 'Cargo pants', tags: 'cargo pants, utility, combat trousers', fabric: 'fabric/canvas', color: 'khaki', group: 'urban' },
  { id: 'jeans', kind: 'bottom', name: 'Jeans', tags: 'jeans, denim trousers', fabric: 'pat/denim', color: 'white', group: 'everyday' },
  { id: 'shorts', kind: 'bottom', name: 'Shorts', tags: 'shorts, summer', fabric: 'fabric/canvas', color: 'navy', group: 'everyday' },
  { id: 'maxi-skirt', kind: 'bottom', name: 'Maxi skirt', tags: 'long skirt, maxi skirt, flowing', fabric: 'fabric/nylon', color: 'burgundy', group: 'everyday' },
  { id: 'greaves', kind: 'bottom', name: 'Armoured legs', tags: 'armour leg plates, greaves, knight', fabric: 'fabric/armor', color: 'steel', group: 'fantasy' },
  // shoes
  { id: 'sneakers', kind: 'shoes', name: 'Sneakers', tags: 'sneakers, trainers, running shoes', fabric: 'fabric/mesh', color: 'white', group: 'urban' },
  { id: 'high-tops', kind: 'shoes', name: 'High-tops', tags: 'high top sneakers, basketball shoes', fabric: 'fabric/leather', color: 'black', group: 'urban' },
  { id: 'boots', kind: 'shoes', name: 'Tall boots', tags: 'tall boots, knee boots, riding boots', fabric: 'fabric/leather', color: 'brown', group: 'everyday' },
  { id: 'work-boots', kind: 'shoes', name: 'Work boots', tags: 'work boots, timberland style, tan boots', fabric: 'fabric/suede', color: 'white', group: 'urban' },
  { id: 'slides', kind: 'shoes', name: 'Slides', tags: 'slides, sandals, slippers', fabric: 'fabric/nylon', color: 'black', group: 'urban' },
  { id: 'armored-boots', kind: 'shoes', name: 'Armoured boots', tags: 'knight boots, sabatons, armoured boots', fabric: 'fabric/armor', color: 'steel', group: 'fantasy' },
  // over
  { id: 'cape', kind: 'over', name: 'Cape', tags: 'cape, superhero cape, cloak', fabric: 'fabric/wool', color: 'red', group: 'fantasy' },
  { id: 'cloak', kind: 'over', name: 'Long cloak', tags: 'long cloak, travelling cloak, mantle', fabric: 'fabric/wool', color: 'charcoal', group: 'fantasy' },
];
export const fabricAsset = (fabric: string) => (fabric.startsWith('pat/') ? `${fabric}.jpg` : `${EXTRAS_ASSETS}${fabric}.jpg`);
/** Thumbnails added after v2 live in extras/thumbs2/ (existing files are never overwritten). */
const THUMBS2 = new Set([
  ...FEATURE_PRESETS.map((f) => `feature-${f.id}`),
  ...['monolid', 'monolid-lifted', 'hooded-almond', 'inner-fold', 'deep-set', 'wide-open'].map((id) => `eye-${id}`),
]);
export const extrasThumb = (kind: string, id: string) => `${EXTRAS_ASSETS}${THUMBS2.has(`${kind}-${id}`) ? 'thumbs2' : 'thumbs'}/${kind}-${id}.webp`;

// ---------- defaults / normalisation ----------

export const DEFAULT_EXTRAS: BuilderExtras = {
  faceShape: 'base', features: 'none', face: { ...FACE_ZERO }, bodyShape: 'base', body: { ...BODY_ZERO },
  eyeShape: 'base', lashes: 'base', brows: 'base', lips: 'none', eyeMakeup: 'none', freckles: 'none',
  facialHair: 'none', beardTexture: 'coily',
  wings: 'none', horns: 'none', ears: 'none', halo: 'none', tail: 'none', markings: 'none', fantasyColor: 'natural',
  top: 'base', bottom: 'base', shoes: 'base', over: 'none',
  topColor: '', bottomColor: '', shoeColor: '', overColor: '',
  lib: {},
};

const pickId = (list: { id: string }[], v: unknown, d: string) => (typeof v === 'string' && list.some((x) => x.id === v) ? v : d);
const num = (v: unknown, lo: number, hi: number) => { const n = Number(v); return Number.isFinite(n) ? Math.max(lo, Math.min(hi, Math.round(n * 100) / 100)) : 0; };
const garment = (kind: GarmentKind, v: unknown, none: string) =>
  typeof v === 'string' && GARMENTS.some((g) => g.id === v && g.kind === kind) ? v : none;

export function normalizeExtras(s: Partial<BuilderExtras> | any): BuilderExtras {
  s ||= {};
  const faceShape = pickId(FACE_SHAPES, s.faceShape, 'base');
  const features = pickId(FEATURE_PRESETS, s.features, 'none');
  const bodyShape = pickId(BODY_SHAPES, s.bodyShape, 'base');
  // sliders default to the presets (face shape + feature preset); explicit sliders win
  const fp = FACE_SHAPES.find((f) => f.id === faceShape)!.face, bp = BODY_SHAPES.find((b) => b.id === bodyShape)!.body;
  const ft = FEATURE_PRESETS.find((f) => f.id === features)!.face;
  const face = {} as FaceSliders;
  for (const k of Object.keys(FACE_ZERO) as (keyof FaceSliders)[]) face[k] = s.face && s.face[k] !== undefined ? num(s.face[k], -1, 1) : num(fp[k] + (ft[k] ?? 0), -1, 1);
  const body = {} as BodySliders;
  for (const d of BODY_SLIDERS) body[d.key] = s.body && s.body[d.key] !== undefined ? num(s.body[d.key], d.min, 1) : bp[d.key];
  const col = (v: unknown) => pickId(GARMENT_COLORS, v, '');
  return {
    faceShape, features, face, bodyShape, body,
    eyeShape: pickId(EYE_SHAPES, s.eyeShape, 'base'),
    lashes: pickId(LASH_STYLES, s.lashes, 'base'),
    brows: pickId(BROW_STYLES, s.brows, 'base'),
    lips: pickId(LIP_STYLES, s.lips, 'none'),
    eyeMakeup: pickId(EYE_MAKEUP, s.eyeMakeup, 'none'),
    freckles: pickId(FRECKLES, s.freckles, 'none'),
    facialHair: pickId(FACIAL_HAIR, s.facialHair, 'none'),
    beardTexture: pickId(BEARD_TEXTURES, s.beardTexture, 'coily'),
    wings: pickId(WINGS, s.wings, 'none'),
    horns: pickId(HORNS, s.horns, 'none'),
    ears: pickId(EARS, s.ears, 'none'),
    halo: pickId(HALOS, s.halo, 'none'),
    tail: pickId(TAILS, s.tail, 'none'),
    markings: pickId(MARKINGS, s.markings, 'none'),
    fantasyColor: pickId(FANTASY_COLORS, s.fantasyColor, 'natural'),
    top: garment('top', s.top, 'base'),
    bottom: garment('bottom', s.bottom, 'base'),
    shoes: garment('shoes', s.shoes, 'base'),
    over: garment('over', s.over, 'none'),
    topColor: col(s.topColor), bottomColor: col(s.bottomColor), shoeColor: col(s.shoeColor), overColor: col(s.overColor),
    lib: normalizeLibrary(s.lib),
  };
}

/** Fields that change the mesh (the live preview rebuilds the VRM when this changes). */
export function geometryKey(x: BuilderExtras): string {
  return JSON.stringify([x.face, x.body, x.eyeShape, x.facialHair, x.beardTexture, x.wings, x.horns, x.ears, x.halo, x.tail, x.markings, x.fantasyColor,
    x.top, x.bottom, x.shoes, x.over, x.topColor, x.bottomColor, x.shoeColor, x.overColor, x.lib]);
}

export const isZero = (o: Record<string, number>) => Object.values(o).every((v) => Math.abs(v) < 1e-3);
export const garmentById = (id: string) => GARMENTS.find((g) => g.id === id);
export const garmentColorHex = (g: GarmentDef, colorId: string) => (GARMENT_COLORS.find((c) => c.id === (colorId || g.color)) ?? GARMENT_COLORS[0]).hex;
/** The fantasy features a selection has, as [field, option] (for removable chips). */
export const FANTASY_FIELDS = ['wings', 'horns', 'ears', 'halo', 'tail', 'markings'] as const;
export type FantasyField = (typeof FANTASY_FIELDS)[number];
export const FANTASY_LISTS: Record<FantasyField, Named[]> = { wings: WINGS, horns: HORNS, ears: EARS, halo: HALOS, tail: TAILS, markings: MARKINGS };
export const FANTASY_NOUN: Record<FantasyField, string> = { wings: 'wings', horns: 'horns', ears: 'ears', halo: '', tail: 'tail', markings: 'markings' };
export function fantasyFeatures(x: BuilderExtras): { field: FantasyField; id: string; label: string }[] {
  return FANTASY_FIELDS.filter((k) => x[k] !== 'none').map((k) => {
    const o = FANTASY_LISTS[k].find((v) => v.id === x[k]);
    const name = o?.name ?? x[k];
    const noun = FANTASY_NOUN[k];
    // "Ram horns", "Cat tail"; names that are already plural nouns stay as they are ("Antlers", "Glow freckles")
    const plain = !noun || /s$/i.test(name) || name.toLowerCase().includes(noun.replace(/s$/, ''));
    return { field: k, id: x[k], label: plain ? name : `${name} ${noun}` };
  });
}
export const fantasyHex = (id: string) => (FANTASY_COLORS.find((c) => c.id === id) ?? FANTASY_COLORS[0]).hex;
